import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight, Plus, Trash2, ChevronUp, ChevronDown,
  Hotel as HotelIcon, X, Navigation, MoreHorizontal, Clock, Edit, Search, ExternalLink, GripVertical
} from 'lucide-react';
import SkeletonActivityCard from './SkeletonActivityCard';
import { DAY_COLORS, computeDayDriveStats, formatDriveStats, getCategoryInfo } from './plannerHelpers';
import { getIcon } from '../../utils/iconResolver';

// ── Vertical activity row (full-width, Polarsteps-style) ────────────────────

const formatDuration = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h${m}m`;
};

function ActivityRow({ activity, onEdit, onDelete, selectedActivityId, onActivitySelect }) {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: activity.id });
  const rowRef = useRef(null);
  const isSelected = selectedActivityId === activity.id;

  const combinedRef = useCallback((el) => {
    setNodeRef(el);
    rowRef.current = el;
  }, [setNodeRef]);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected]);

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  const cat = getCategoryInfo(activity.category);
  const CatIcon = getIcon(cat.iconName);
  const duration = formatDuration(activity.duration_minutes);

  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
    [activity.title, activity.location].filter(Boolean).join(' ')
  )}`;

  return (
    <div
      ref={combinedRef}
      style={sortableStyle}
      className={`bg-white rounded-xl border overflow-hidden shadow-sm group/row transition-colors ${
        isDragging ? 'shadow-xl z-50' : ''
      } ${isSelected ? 'border-l-4 border-coral-400 border-t-platinum-100 border-r-platinum-100 border-b-platinum-100' : 'border-platinum-100'}`}
    >
      {/* Main row */}
      <div
        className="flex items-center gap-2 pr-4 py-3.5 cursor-pointer"
        onClick={() => {
          if (isDragging) return;
          setExpanded(e => !e);
          if (onActivitySelect) onActivitySelect(isSelected ? null : activity.id);
        }}
      >
        {/* Drag handle — explicit grip strip */}
        <div
          className="flex items-center justify-center w-8 flex-shrink-0 self-stretch cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={16} className="text-platinum-300 group-hover/row:text-platinum-400 transition-colors" />
        </div>

        {/* Category icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: cat.color + '20' }}
        >
          <CatIcon size={18} style={{ color: cat.color }} />
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-charcoal-500 leading-snug truncate">
            {activity.title}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {activity.location && (
              <span className="text-sm text-platinum-400 truncate">{activity.location}</span>
            )}
            {duration && activity.category === 'transport' && (
              <span className="flex items-center gap-0.5 text-sm text-platinum-400 flex-shrink-0">
                <Clock size={11} /> {duration}
              </span>
            )}
          </div>
        </div>

        {/* Right side: time + hover actions + chevron */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {activity.time_of_day && (
            <span className="text-sm text-platinum-400 capitalize">{activity.time_of_day}</span>
          )}
          <div className="flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onEdit(activity); }}
              className="p-1.5 text-platinum-400 hover:text-coral-500 hover:bg-coral-50 rounded-lg transition-colors"
            >
              <Edit size={13} />
            </button>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onDelete(activity.id); }}
              className="p-1.5 text-platinum-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
          <ChevronDown
            size={14}
            className={`text-platinum-300 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Expanded detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-3 border-t border-platinum-100 bg-platinum-50/50 space-y-2">
              {activity.description && (
                <p className="text-sm text-charcoal-400 leading-relaxed">{activity.description}</p>
              )}
              <a
                href={searchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-coral-500 hover:text-coral-600 hover:underline w-fit"
                onClick={(e) => e.stopPropagation()}
              >
                <Search size={10} />
                Search on Google
                <ExternalLink size={9} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CollapsibleDaySection({
  day,
  dayActivities,
  isDragOver,
  dayLabel,
  dayTitle,
  autoTitle,
  onDayTitleSave,
  dayAccommodation,
  skeletonCount,
  generatingActivities,
  isHighlighted,
  itinerary,
  onDrop,
  onDragOver,
  onDragLeave,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onSaveNotes,
  onDeleteAccommodation,
  onAddAccommodation,
  onHotelDrop,
  onDeleteDay,
  onSwapDays,
  totalDays,
  expanded,
  onToggle,
  selectedActivityId,
  onActivitySelect,
}) {
  const [localTitle, setLocalTitle] = useState(dayTitle || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const menuButtonRef = useRef(null);

  useEffect(() => { setLocalTitle(dayTitle || ''); }, [dayTitle]);

  // The day card uses overflow-hidden for its rounded corners, which clips any
  // absolutely-positioned child. Render the menu in a portal with fixed coords
  // so it stays reachable when the day is collapsed.
  const openMenu = () => {
    const rect = menuButtonRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  const dayColor = DAY_COLORS[(day - 1) % DAY_COLORS.length];
  const isEmpty = dayActivities.length === 0 && !generatingActivities;
  const emptyCollapsed = isEmpty && !expanded;
  const isRoadTrip = ['car', 'van', 'mixed'].includes(itinerary?.travel_mode);
  const driveStats = useMemo(
    () => isRoadTrip ? computeDayDriveStats(dayActivities) : null,
    [dayActivities, isRoadTrip] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const driveLabel = formatDriveStats(driveStats);

  // Category dots for collapsed summary
  const categoryDots = useMemo(() => {
    const seen = new Set();
    return dayActivities
      .map(a => getCategoryInfo(a.category))
      .filter(c => { if (seen.has(c.color)) return false; seen.add(c.color); return true; })
      .slice(0, 5);
  }, [dayActivities]);

  const handleTitleBlur = () => {
    const trimmed = localTitle.trim();
    if (trimmed !== (dayTitle || '')) {
      onDayTitleSave?.(day, trimmed);
    }
  };

  // When dayLabel is "Day X" (no start date set), don't show it as a subtitle
  const dateSubtitle = dayLabel && !dayLabel.startsWith('Day ') ? dayLabel : null;

  // Default display title: "Paris Day 1", or just "Day 1" if no city known
  const autoDisplayTitle = autoTitle || null;

  // Activity synthesis for collapsed header: "Louvre · Seine cruise · Montmartre"
  const activitySynthesis = useMemo(() => {
    if (dayActivities.length === 0) return null;
    return dayActivities
      .slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .slice(0, 3)
      .map(a => a.title)
      .join(' · ');
  }, [dayActivities]);

  return (
    <div
      className={`rounded-2xl overflow-hidden transition-all ${
        emptyCollapsed ? 'shadow-none' : 'shadow-sm'
      } ${
        isDragOver
          ? 'ring-2 ring-coral-400 shadow-lg'
          : isHighlighted
            ? 'ring-2 ring-coral-300'
            : ''
      }`}
      style={emptyCollapsed ? { border: `1.5px solid #e8e8e8`, borderLeft: `4px solid ${dayColor}` } : {}}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {/* ── Day header: coral when has content, white+border when empty+collapsed ── */}
      <div
        className={`flex items-center gap-3 px-5 py-4 cursor-pointer select-none ${
          emptyCollapsed ? 'bg-white' : 'bg-coral-400'
        }`}
        onClick={() => onToggle(day)}
      >
        {/* Day number block */}
        <div
          className="flex-shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center"
          style={{ backgroundColor: emptyCollapsed ? dayColor + '18' : 'rgba(0,0,0,0.1)' }}
        >
          <span className={`text-[9px] font-bold uppercase tracking-widest leading-none ${emptyCollapsed ? 'text-charcoal-300' : 'text-white/70'}`}>Day</span>
          <span className={`text-2xl font-black leading-tight`} style={{ color: emptyCollapsed ? dayColor : 'white' }}>{day}</span>
        </div>

        {/* Title + date */}
        <div className="flex-1 min-w-0">
          {onDayTitleSave && expanded ? (
            <input
              type="text"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
              onClick={(e) => e.stopPropagation()}
              placeholder={autoDisplayTitle || 'Add a title...'}
              className={`font-semibold text-base bg-transparent outline-none w-full leading-tight ${emptyCollapsed ? 'text-charcoal-500 placeholder:text-charcoal-300' : 'text-white placeholder:text-white/40'}`}
            />
          ) : (
            <p className={`font-semibold text-base truncate leading-tight ${emptyCollapsed ? 'text-charcoal-400' : 'text-white'}`}>
              {dayTitle || autoDisplayTitle || (emptyCollapsed ? 'Empty day' : '')}
            </p>
          )}
          {dateSubtitle && (
            <p className={`text-xs mt-0.5 ${emptyCollapsed ? 'text-charcoal-300' : 'text-white/70'}`}>{dateSubtitle}</p>
          )}
          {!expanded && activitySynthesis && (
            <p className="text-white/50 text-xs mt-0.5 truncate">{activitySynthesis}</p>
          )}
        </div>

        {/* Collapsed summary */}
        {!expanded && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {emptyCollapsed ? (
              <span className="text-xs px-2.5 py-1 rounded-full font-medium text-charcoal-300 border border-platinum-200">
                + Add activities
              </span>
            ) : (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium text-white bg-black/15">
              {dayActivities.length} {dayActivities.length === 1 ? 'activity' : 'activities'}
            </span>
            )}
            {driveLabel && (
              <span className="text-xs text-white/70 flex items-center gap-0.5 flex-shrink-0">
                <Navigation size={10} /> {driveLabel}
              </span>
            )}
            <div className="flex items-center gap-0.5">
              {categoryDots.map((c, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-white/40" />
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onAddActivity(day)}
            className={`p-2 rounded-lg transition-colors ${emptyCollapsed ? 'text-charcoal-300 hover:text-coral-400 hover:bg-coral-50' : 'text-white/70 hover:text-white hover:bg-white/15'}`}
            title="Add activity"
          >
            <Plus size={15} />
          </button>
          <div className="relative">
            <button
              ref={menuButtonRef}
              onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
              className={`p-2 rounded-lg transition-colors ${emptyCollapsed ? 'text-charcoal-300 hover:text-coral-400 hover:bg-coral-50' : 'text-white/70 hover:text-white hover:bg-white/15'}`}
              title="Day options"
            >
              <MoreHorizontal size={15} />
            </button>
            {menuOpen && menuPos && createPortal(
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setMenuOpen(false)} />
                <div
                  className="fixed z-[61] bg-white rounded-xl shadow-lg border border-platinum-100 py-1 min-w-[150px]"
                  style={{ top: menuPos.top, right: menuPos.right }}
                >
                  {onSwapDays && day > 1 && (
                    <button
                      onClick={() => { onSwapDays(day, day - 1); setMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-charcoal-500 hover:bg-platinum-50"
                    >
                      <ChevronUp size={13} /> Move up
                    </button>
                  )}
                  {onSwapDays && day < totalDays && (
                    <button
                      onClick={() => { onSwapDays(day, day + 1); setMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-charcoal-500 hover:bg-platinum-50"
                    >
                      <ChevronDown size={13} /> Move down
                    </button>
                  )}
                  {onDeleteDay && (
                    <button
                      onClick={() => { onDeleteDay(day); setMenuOpen(false); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={13} /> Delete day
                    </button>
                  )}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        {/* Expand chevron */}
        <ChevronRight
          size={16}
          className={`text-white/60 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {/* ── Expanded content ── */}
      {/*
        SortableContext items must stay mounted (hidden) when collapsed
        so @dnd-kit refs stay registered for cross-day drag-drop.
      */}
      <div style={{ display: expanded ? 'block' : 'none' }} className="bg-platinum-50">
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="px-5 pb-5 pt-4"
            >
              {/* Accommodation row */}
              {dayAccommodation ? (
                <div className="flex items-center gap-2 text-xs text-columbia-700 bg-white px-3 py-1.5 rounded-xl mb-3 border border-columbia-200 shadow-sm group/acc">
                  <HotelIcon size={12} />
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(dayAccommodation.name + ' ' + (dayAccommodation.location || itinerary?.destination || ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium hover:text-coral-500 transition-colors"
                  >
                    {dayAccommodation.name}
                  </a>
                  {dayAccommodation.price_per_night && (
                    <span className="text-columbia-500">${dayAccommodation.price_per_night}/night</span>
                  )}
                  <button
                    onClick={() => onDeleteAccommodation(dayAccommodation.id)}
                    className="ml-auto opacity-0 group-hover/acc:opacity-100 text-platinum-400 hover:text-red-500 transition-all"
                    title="Remove accommodation"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    const name = window.prompt('Enter hotel/accommodation name:');
                    if (name && name.trim()) {
                      onAddAccommodation({ name: name.trim(), type: 'hotel' }, day, day + 1);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const rawData = e.dataTransfer.getData('application/json');
                    if (rawData) {
                      const data = JSON.parse(rawData);
                      if (data.__type === 'hotel') {
                        onHotelDrop({ hotel: data, dayNumber: day });
                      }
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.add('border-columbia-400', 'bg-columbia-50', 'text-columbia-600');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-columbia-400', 'bg-columbia-50', 'text-columbia-600');
                  }}
                  className="flex items-center gap-2 text-xs text-platinum-500 px-3 py-1.5 rounded-xl mb-3 border border-dashed border-white/60 bg-white/40 w-full hover:border-columbia-300 hover:text-columbia-600 hover:bg-white/70 transition-all cursor-pointer"
                >
                  <HotelIcon size={12} />
                  <span className="italic">Accommodation to be defined</span>
                </button>
              )}


              {/* Drop indicator */}
              {isDragOver && (
                <div className="mb-2 p-2 bg-coral-100 rounded-lg text-center">
                  <p className="text-sm text-coral-700 font-medium">Drop here to add to {dayLabel}</p>
                </div>
              )}

              {/* Activities — vertical list */}
              {dayActivities.length === 0 && !isDragOver ? (
                generatingActivities ? (
                  <div className="flex flex-col gap-2">
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <div key={i} className="w-full h-16 rounded-xl bg-platinum-100 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => onAddActivity(day)}
                    className="w-full p-8 rounded-xl border-2 border-dashed border-platinum-200 hover:border-coral-300 hover:bg-coral-50 transition-all group text-center"
                  >
                    <div className="w-11 h-11 rounded-full bg-platinum-100 group-hover:bg-coral-100 flex items-center justify-center mx-auto mb-3 transition-colors">
                      <Plus size={20} className="text-platinum-400 group-hover:text-coral-400 transition-colors" />
                    </div>
                    <p className="text-sm font-semibold text-charcoal-400 group-hover:text-coral-500 transition-colors">Add your first activity</p>
                    <p className="text-xs text-platinum-400 mt-1">Click here, or drag one in from the AI panel</p>
                  </button>
                )
              ) : (
                <div className="flex flex-col gap-2">
                  <SortableContext
                    items={dayActivities.map(a => a.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {dayActivities.map(activity => (
                      <ActivityRow
                        key={activity.id}
                        activity={activity}
                        onEdit={onEditActivity}
                        onDelete={onDeleteActivity}
                        selectedActivityId={selectedActivityId}
                        onActivitySelect={onActivitySelect}
                      />
                    ))}
                  </SortableContext>
                  <button
                    onClick={() => onAddActivity(day)}
                    className="w-full py-2.5 rounded-xl border-2 border-dashed border-platinum-200 flex items-center justify-center gap-1.5 text-xs text-platinum-400 hover:border-coral-300 hover:text-coral-400 hover:bg-coral-50 transition-colors"
                  >
                    <Plus size={14} /> Add activity
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hidden SortableContext when collapsed — keeps @dnd-kit refs registered */}
      {!expanded && dayActivities.length > 0 && (
        <div style={{ display: 'none' }}>
          <SortableContext
            items={dayActivities.map(a => a.id)}
            strategy={verticalListSortingStrategy}
          >
            {dayActivities.map(activity => (
              <ActivityRow
                key={activity.id}
                activity={activity}
                onEdit={onEditActivity}
                onDelete={onDeleteActivity}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
}
